import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toastr } from 'react-redux-toastr';
import { Link } from "react-router-dom";
import {
	Button, Card, CardBody, CardHeader, Col, Container, Row, Nav, NavItem, NavLink,
	Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Input
} from "reactstrap";
import { confirmAlert } from "react-confirm-alert";
import general_fetch from "../../utils/fetch";
import DeleteNotification from "../../components/DeleteNotification";
import { date_string } from "../../utils/date_string";
import ISearch from "../../components/ISearch/index.jsx";
import NA from "../../components/NA/index.js";
import "./style.css";

// Mirrors the small helper used by components/Dues/index.jsx so confirm dialogs look consistent.
const submit_confirm = (yes_action, message, button_name) => {
	confirmAlert({
		title: 'Confirm to submit',
		message: message,
		customUI: ({ onClose }) => (
			<div className='custom-ui'>
				<DeleteNotification yes_del={yes_action} no_del={() => {}} onClose={onClose} text={message} button_name={button_name} />
			</div>
		)
	});
};

const format_currency = (amount, currency) => {
	if (amount == null) return '-';
	const symbol = currency === 'USD' ? '$' : '₹';
	return `${symbol}${Number(amount).toLocaleString('en-IN')}`;
};

const RejectOfflinePaymentModal = ({ open, toggle, payment, on_submit, submitting }) => {
	const [reason, set_reason] = useState('');

	useEffect(() => {
		if (!open) set_reason('');
	}, [open]);

	const handle_submit = () => {
		if (!reason.trim()) {
			toastr.error('Please provide a reason for rejection');
			return;
		}
		on_submit(reason.trim());
	};

	return (
		<Modal isOpen={open} toggle={toggle} className="modal-dialog-centered" size="md">
			<ModalHeader toggle={toggle}>Reject Offline Payment</ModalHeader>
			<ModalBody style={{ fontSize: '14px' }}>
				{payment ? (
					<div className="mb-3" style={{ background: '#fafafa', padding: '10px 12px', borderRadius: '6px' }}>
						<div><b>Org:</b> {payment.store_details ? payment.store_details.name : payment.order_details ? payment.order_details.store_id : '-'}</div>
						<div><b>Order:</b> {payment.order_id}</div>
						<div><b>Amount:</b> {format_currency(payment.amount, payment.currency)}</div>
						<div><b>Mode:</b> {payment.payment_mode || '-'}{payment.payment_reference ? ` (Ref: ${payment.payment_reference})` : ''}</div>
					</div>
				) : null}
				<FormGroup>
					<label className="form-control-label">Reason for rejection <span style={{ color: '#ff4d4f' }}>*</span></label>
					<Input
						type="textarea"
						rows={4}
						placeholder="e.g. Amount mismatch, illegible proof, wrong account..."
						value={reason}
						onChange={(e) => set_reason(e.target.value)}
					/>
					<small className="text-muted">
						Rejecting will revert the order's due date to {payment && payment.original_due_date ? date_string(payment.original_due_date) : 'its original value'} and re-suspend the org if it was suspended before submission.
					</small>
				</FormGroup>
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" size="sm" onClick={toggle} disabled={submitting}>Cancel</Button>
				<Button color="danger" size="sm" onClick={handle_submit} disabled={submitting || !reason.trim()}>
					{submitting ? 'Rejecting...' : 'Reject Payment'}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

const HELPER_OPEN_KEY = 'pending_offline_payments_helper_open_v2';

const HelperBanner = () => {
	// Default closed — opt-in expand, choice persisted per-rep.
	const [open, set_open] = useState(() => {
		try { return localStorage.getItem(HELPER_OPEN_KEY) === '1'; } catch (e) { return false; }
	});

	const toggle = () => {
		const next = !open;
		set_open(next);
		try { localStorage.setItem(HELPER_OPEN_KEY, next ? '1' : '0'); } catch (e) {}
	};

	return (
		<div className="pending_payments_helper">
			<span className="pending_payments_helper_strip" onClick={toggle}>
				<i className={`fa ${open ? 'fa-caret-down' : 'fa-caret-right'}`} />
				<i className="fa fa-info-circle" />
				<span>How this works</span>
			</span>
			{open ? (
				<div className="pending_payments_helper_panel">
					Clients with dues can pay offline (NEFT / UPI / cheque / bank transfer) and upload proof from
					their <b>Admin → Billing → Dues</b> page. Each submission lands here for CS to review.
					<ol>
						<li>Submission grants the org <b>+2 days of access</b> while you verify — it is <b>not</b> confirmation.</li>
						<li>Open <b>View Proof</b>, match amount / mode / UTR against the dues and our bank statement.</li>
						<li><b>Confirm</b> finalizes the payment (same path as "Record Manual Payment"). <b>Reject</b> reverts the due date and re-suspends if they were suspended before — reason is required and goes to G-Chat.</li>
					</ol>
					<div className="pending_payments_helper_aside">
						If the client paid online during review, Confirm will surface "Order already paid" and the system clears the stale submission — just refresh.
					</div>
				</div>
			) : null}
		</div>
	);
};

const PendingOfflinePayments = ({
	user_details, cs_org_users, all_stores, set_page_loader,
	is_cs_admin, is_team_lead, infurnia_teams,
	pending_offline_payments, refresh_pending_offline_payments
}) => {
	const [search_text, set_search_text] = useState('');
	const [active_tab, set_active_tab] = useState(null);
	const [reject_modal_open, set_reject_modal_open] = useState(false);
	const [active_payment, set_active_payment] = useState(null);
	const [submitting, set_submitting] = useState(false);

	// Build a quick lookup of store -> CSE id from all_stores so we can scope by Mine / My Team / All.
	const store_id_to_cse_id = useMemo(() => {
		const map = {};
		(all_stores || []).forEach(s => {
			if (s && s.id) map[s.id] = s.customer_success_executive_id || null;
		});
		return map;
	}, [all_stores]);

	// CSE ids that belong to teams the current user leads.
	const my_team_cse_ids = useMemo(() => {
		if (!user_details || !user_details.id) return new Set();
		const ids = new Set();
		(infurnia_teams || []).forEach(team => {
			if (team && team.team_lead_id === user_details.id && Array.isArray(team.users)) {
				team.users.forEach(uid => ids.add(uid));
			}
		});
		// Team leads naturally include themselves.
		ids.add(user_details.id);
		return ids;
	}, [infurnia_teams, user_details]);

	// Default tab depends on role.
	useEffect(() => {
		if (active_tab !== null) return;
		if (is_cs_admin) set_active_tab('all');
		else if (is_team_lead) set_active_tab('team');
		else set_active_tab('mine');
	}, [is_cs_admin, is_team_lead, active_tab]);

	// First fetch on mount; rely on Admin.jsx's polling for refreshes thereafter.
	useEffect(() => {
		if (typeof refresh_pending_offline_payments === 'function') {
			refresh_pending_offline_payments();
		}
	}, []);

	const cse_name_for_store = useCallback((store_id) => {
		const cse_id = store_id_to_cse_id[store_id];
		if (!cse_id) return null;
		const user = (cs_org_users || []).find(u => u.id === cse_id);
		return user ? user.first_name : null;
	}, [store_id_to_cse_id, cs_org_users]);

	const filtered_payments = useMemo(() => {
		const list = Array.isArray(pending_offline_payments) ? pending_offline_payments : [];
		const filtered = list.filter(p => {
			const store_id = p.order_details ? p.order_details.store_id : (p.store_details ? p.store_details.id : null);
			const cse_id = store_id ? store_id_to_cse_id[store_id] : null;

			if (active_tab === 'mine') {
				if (cse_id !== (user_details && user_details.id)) return false;
			} else if (active_tab === 'team') {
				if (!cse_id || !my_team_cse_ids.has(cse_id)) return false;
			}

			if (search_text && search_text.trim()) {
				const needle = search_text.trim().toLowerCase();
				const org_name = p.store_details && p.store_details.name ? p.store_details.name.toLowerCase() : '';
				const order_id = (p.order_id || '').toLowerCase();
				const ref = (p.payment_reference || '').toLowerCase();
				if (!org_name.includes(needle) && !order_id.includes(needle) && !ref.includes(needle)) return false;
			}
			return true;
		});
		// Newest submissions first.
		filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
		return filtered;
	}, [pending_offline_payments, active_tab, store_id_to_cse_id, my_team_cse_ids, user_details, search_text]);

	const refetch_after_action = async () => {
		if (typeof refresh_pending_offline_payments === 'function') {
			await refresh_pending_offline_payments();
		}
	};

	const confirm_payment = async (payment) => {
		try {
			set_page_loader(true, 'Confirming payment...');
			await general_fetch({ url: 'csp/confirm_offline_payment', body: { payment_id: payment.id } });
			set_page_loader(false);
			toastr.success('Offline payment confirmed');
			await refetch_after_action();
		} catch (err) {
			set_page_loader(false);
			console.error('confirm_offline_payment failed', err);
			// Backend throws ORDER_ALREADY_PAID after cleaning up; refetch so the row disappears.
			const msg = err && err.err && err.err.message ? err.err.message : '';
			if (msg.includes('ORDER_ALREADY_PAID')) {
				toastr.warning('Order was already paid via online payment. The pending submission has been cleared.');
				await refetch_after_action();
			} else {
				toastr.error('Could not confirm the offline payment, please try again.');
			}
		}
	};

	const reject_payment = async (reason) => {
		if (!active_payment) return;
		try {
			set_submitting(true);
			set_page_loader(true, 'Rejecting payment...');
			await general_fetch({ url: 'csp/reject_offline_payment', body: { payment_id: active_payment.id, reason } });
			set_page_loader(false);
			set_submitting(false);
			set_reject_modal_open(false);
			set_active_payment(null);
			toastr.success('Offline payment rejected');
			await refetch_after_action();
		} catch (err) {
			set_page_loader(false);
			set_submitting(false);
			console.error('reject_offline_payment failed', err);
			toastr.error('Could not reject the offline payment, please try again.');
		}
	};

	const open_reject_modal = (payment) => {
		set_active_payment(payment);
		set_reject_modal_open(true);
	};

	const counts_by_tab = useMemo(() => {
		const list = Array.isArray(pending_offline_payments) ? pending_offline_payments : [];
		let mine = 0, team = 0;
		list.forEach(p => {
			const store_id = p.order_details ? p.order_details.store_id : (p.store_details ? p.store_details.id : null);
			const cse_id = store_id ? store_id_to_cse_id[store_id] : null;
			if (cse_id && user_details && cse_id === user_details.id) mine += 1;
			if (cse_id && my_team_cse_ids.has(cse_id)) team += 1;
		});
		return { mine, team, all: list.length };
	}, [pending_offline_payments, store_id_to_cse_id, my_team_cse_ids, user_details]);

	return (
		<>
			<RejectOfflinePaymentModal
				open={reject_modal_open}
				toggle={() => { set_reject_modal_open(false); set_active_payment(null); }}
				payment={active_payment}
				on_submit={reject_payment}
				submitting={submitting}
			/>
			<Container fluid className="pending_payments_container">
				<Card className="shadow pending_payments_card">
					<CardHeader className="pending_payments_header">
						<div className="pending_payments_title">Pending Offline Payments</div>
						<div className="d-flex" style={{ alignItems: 'center', gap: '12px' }}>
							<ISearch
								search_text={search_text}
								set_search_text={set_search_text}
								placeholder="Search org / order / UTR"
								style_outer={{ width: '260px' }}
							/>
							<Button color="primary" outline size="sm" onClick={refetch_after_action}>
								<i className="fa fa-refresh mr-1" /> Refresh
							</Button>
						</div>
					</CardHeader>
					<HelperBanner />
					<div style={{ padding: '8px 16px 0 16px' }}>
						<Nav pills>
							<NavItem>
								<NavLink href="#" active={active_tab === 'mine'} onClick={() => set_active_tab('mine')}>
									<b>Mine</b> ({counts_by_tab.mine})
								</NavLink>
							</NavItem>
							{(is_team_lead || is_cs_admin) && (
								<NavItem>
									<NavLink href="#" active={active_tab === 'team'} onClick={() => set_active_tab('team')}>
										<b>My Team</b> ({counts_by_tab.team})
									</NavLink>
								</NavItem>
							)}
							{is_cs_admin && (
								<NavItem>
									<NavLink href="#" active={active_tab === 'all'} onClick={() => set_active_tab('all')}>
										<b>All</b> ({counts_by_tab.all})
									</NavLink>
								</NavItem>
							)}
						</Nav>
					</div>
					<div className="pending_payments_table_wrapper">
						{filtered_payments.length === 0 ? (
							<div className="pending_payments_empty">
								<div style={{ textAlign: 'center' }}>
									<i className="fa fa-check-circle" style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} /><br />
									No pending offline payments to review.
								</div>
							</div>
						) : (
							<table className="align-items-center table table-flush" style={{ fontSize: '13px', width: '100%' }}>
								<thead className="thead-light">
									<tr>
										<th>Org</th>
										<th>Order</th>
										<th className="text-right">Amount</th>
										<th>Mode / UTR</th>
										<th>Submitted</th>
										<th>CS Rep</th>
										<th>Status</th>
										<th className="text-center">Actions</th>
									</tr>
								</thead>
								<tbody>
									{filtered_payments.map((payment) => {
										const store = payment.store_details || {};
										const store_id = (payment.order_details && payment.order_details.store_id) || store.id;
										const cse_name = cse_name_for_store(store_id);
										return (
											<tr key={payment.id}>
												<td>
													<Link to={`/home/store_dashboard/${store_id}/3`} target="_blank">
														{store.name || store_id || <NA />}
													</Link>
													<div className="text-muted" style={{ fontSize: '11px' }}>{store_id}</div>
												</td>
												<td>
													<div>{payment.order_id}</div>
													{payment.order_details && payment.order_details.due_date && (
														<div className="text-muted" style={{ fontSize: '11px' }}>
															Due: {date_string(payment.order_details.due_date)}
														</div>
													)}
												</td>
												<td className="text-right">
													<b>{format_currency(payment.amount, payment.currency)}</b>
												</td>
												<td>
													<div style={{ textTransform: 'uppercase', fontSize: '12px' }}>{payment.payment_mode || '-'}</div>
													<div className="text-muted" style={{ fontSize: '11px' }}>
														{payment.payment_reference ? payment.payment_reference : 'No reference'}
													</div>
												</td>
												<td>
													<div>{payment.created_at ? date_string(payment.created_at) : <NA />}</div>
													{payment.original_due_date && (
														<div className="text-muted" style={{ fontSize: '11px' }}>
															Original due: {date_string(payment.original_due_date)}
														</div>
													)}
												</td>
												<td>{cse_name || <NA />}</td>
												<td>
													<span className="pending_payment_status_pill pending_payment_status_review">Under Review</span>
													{store.suspended ? (
														<span className="pending_payment_status_pill pending_payment_status_suspended">Was Suspended</span>
													) : null}
													{store.churned ? (
														<span className="pending_payment_status_pill pending_payment_status_churned">Was Churned</span>
													) : null}
												</td>
												<td className="text-center">
													<Button
														color="secondary" outline size="sm"
														className="mr-1 pending_payment_action_btn"
														disabled={!payment.proof_of_payment}
														onClick={() => payment.proof_of_payment && window.open(payment.proof_of_payment, '_blank', 'noopener,noreferrer')}
														title="Open proof of payment"
													>
														<i className="fa fa-file mr-1" /> Proof
													</Button>
													<Button
														color="success" size="sm"
														className="mr-1 pending_payment_action_btn"
														onClick={() => submit_confirm(
															() => confirm_payment(payment),
															`Confirm offline payment of ${format_currency(payment.amount, payment.currency)} for ${store.name || store_id}? This will mark the order as paid.`,
															'Confirm'
														)}
													>
														<i className="fa fa-check mr-1" /> Confirm
													</Button>
													<Button
														color="danger" outline size="sm"
														className="pending_payment_action_btn"
														onClick={() => open_reject_modal(payment)}
													>
														<i className="fa fa-times mr-1" /> Reject
													</Button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						)}
					</div>
				</Card>
			</Container>
		</>
	);
};

export default PendingOfflinePayments;
